import { FC, useCallback, useEffect, useState } from 'react'

import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { getAnnualReviewDetailUrl } from 'bank-annual-review/src/Routes/AnnualReview/route'
import { AddNewArDialog, NewArFormValue } from 'bank-annual-review/src/components'
import { AnnualReview } from 'commons/types/DTO/bank/companyDetails/annualReview'
import { TableDataQa } from 'commons/types/enums'
import { Request } from 'commons/utils/request'
import { Layer, PageSectionPrimary, Table, Toast } from 'ui/components'
import { Button } from 'ui/components/Buttons'

import { handlerServerError } from '@orbit/errors'

import { CompanyAnnualReviewTableProps } from './CompanyAnnualReviewTableProps'
import { columns } from './columns'
import { useComponentsEssence } from './hooks/useComponentsEssence'
import { useFilterCompanyAnnualReviewWebPage } from './hooks/useFilterCompanyAnnualReviewWebPage'

export const CompanyAnnualReviewTable: FC<CompanyAnnualReviewTableProps> = (props) => {
  const {
    data: {
      id,
      showAnnualReviews,
      showAnnualReviewsTotalCount,
      companyName,
      showAnnualReviewFinancialsPeriodEndLabel,
      showAnnualReviewFinancialsPeriodStartLabel
    }
  } = props
  const navigate = useNavigate()

  const [webPageData, setWebPageData] = useState({
    annualReviews: showAnnualReviews,
    annualReviewsTotalCount: showAnnualReviewsTotalCount
  })

  useEffect(() => {
    setWebPageData({
      annualReviews: showAnnualReviews,
      annualReviewsTotalCount: showAnnualReviewsTotalCount
    })
  }, [showAnnualReviews, showAnnualReviewsTotalCount])

  const { routine: filterCompanyAnnualReviewWebPage } = useFilterCompanyAnnualReviewWebPage({
    onSuccess: async (...[, result]) => setWebPageData(result),
    onError: (error) => handlerServerError(error)
  })

  const { changePagination, loading, tableState, DeleteConfirmation, onDeleteReview } = useComponentsEssence(
    id,
    filterCompanyAnnualReviewWebPage,
    webPageData.annualReviewsTotalCount
  )

  const { mutateAsync: addAnnualReview } = useMutation<
    { id: number },
    Error,
    NewArFormValue & {
      companyId: number
    }
  >({
    mutationFn: (opts) => Request.post(Request.urls.bank.annualReview, opts),
    onSuccess: async (response) => {
      Toast.successSmall('Annual Review was created.')
      if (response.id) {
        navigate({ to: getAnnualReviewDetailUrl({ companyId: id, id: response.id }) })
      }
    },
    onError: (error) => handlerServerError(error)
  })

  const [showAddModal, setShowAddModal] = useState(false)

  const onAddReview = useCallback(
    async (formValue: NewArFormValue) => await addAnnualReview({ ...formValue, companyId: id }),
    [addAnnualReview, id]
  )

  return (
    <PageSectionPrimary
      face="outlined"
      title="Annual Reviews"
      anchor="annual"
      actions={
        <Button face="tertiary" onClick={() => setShowAddModal(true)}>
          Add New
        </Button>
      }
    >
      <DeleteConfirmation />
      {showAddModal && (
        <AddNewArDialog
          closeDialog={() => setShowAddModal(false)}
          onSave={onAddReview}
          companyName={companyName}
          showAnnualReviewFinancialsPeriodEndLabel={showAnnualReviewFinancialsPeriodEndLabel}
          showAnnualReviewFinancialsPeriodStartLabel={showAnnualReviewFinancialsPeriodStartLabel}
        />
      )}
      <Layer rounded shadowed stretch>
        <Table<AnnualReview>
          dataQa={TableDataQa.BP_COMPANY_ANNUAL_REVIEW}
          columns={columns({ onDeleteReview, id })}
          dataSource={webPageData.annualReviews.map((el) => ({
            item: el
          }))}
          loading={loading}
          pagination={{
            page: tableState.page,
            size: tableState.size,
            onChange: changePagination,
            total: webPageData.annualReviewsTotalCount
          }}
        />
      </Layer>
    </PageSectionPrimary>
  )
}
